import React, {
	useCallback,
	useEffect,
	useReducer,
	useMemo,
} from 'react';
import { Values } from '@shared/utils/objectEnums';
import {
	Currencies,
	EstimatePortActionTypes,
	LinerCostUnitType,
	PortActionTypes,
} from '@shared/utils/constants';
import { SomePartial } from '@shared/utils/generics';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { EstimatePortRotationEntryProps } from '@api/models/estimate-port-rotation-entry';
import type { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import type { Port } from '@api/utils/ports';
import type OrgCanalTransitDefaults from '@api/models/org-canal-transit-defaults';
import type { UpdateFixtureAttributes } from '@api/features/fixtures/updateFixture';
import { PortCallActionProps } from '@api/models/port-call-action';
import { RobBunkerProps } from '@api/models/rob-bunker';
import { getUserData } from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';

export type LocalPortRotationWithKey = LocalPortRotation & { key: string };

export type PartialPortRotationEntry = SomePartial<EstimatePortRotationEntryProps, ('estimateId' | 'id')> & {
	linerCostPricePerUnit: number | null;
	linerCostCurrency: Values<typeof Currencies>;
	linerCostExchangeRate: number | null;
	linerCostUnit: LinerCostUnitType | null;
};

export type NewPortRotationEntryFormValues = {
	type: Values<typeof PortActionTypes | typeof EstimatePortActionTypes>;
	portId: number;
	daysInPort: number | null;
	portCost: number | null;
	linerCostPricePerUnit: number | null;
	consumptionSetId?: number | null;
};

type NewPortRotationEntry = {
	id: number;
	type: Values<typeof PortActionTypes | typeof EstimatePortActionTypes>;
	estimateId: number;
	portCost: number | null;
	linerCostPricePerUnit: number | null;
	generatedBy: null;
	port: Port;
	idleDays: number | null;
	speed: number;
	seaMargin?: number | null;
	consumptionSetId?: number | null;
	PortCallActions: LocalPortRotation['PortCallActions'];
};

export type LocalPortRotation = (EstimatePortRotationEntryProps | PartialPortRotationEntry) & {
	id: number;
	qty: number | undefined;
	timeAllowedOverride: number | null;
	speed: number;
	linerCostPricePerUnit: number | null;
	linerCostCurrency: Values<typeof Currencies>;
	linerCostExchangeRate: number | null;
	linerCostUnit: LinerCostUnitType | null;
	consumptionSetId: number | null;
	hasCargoRelet: boolean;
	PortCallActions: Array<PortCallActionProps & {
		RobBunkers: Array<RobBunkerProps>;
	}>
};

type PortRotationState = {
	localPortRotation: LocalPortRotation[] | null;
	pendingPortRotationChanges: LocalPortRotation[];
	pendingPortRotationOrderChanges: LocalPortRotation[];
};

type PortRotationAction =
	| { type: 'SET_LOCAL'; payload: LocalPortRotation[] | null; update?: boolean } |
	{ type: 'UPDATE_ROW'; payload: { id: number | undefined; attributes: PartialPortRotationEntry } } |
	{ type: 'UPDATE_ROUTE_OPTIONS'; payload: { id: number | undefined; routeOptions: SeaPassageSettings } } |
	{ type: 'SET_ORDER'; payload: LocalPortRotation[] } |
	{ type: 'DELETE_ENTRY'; payload: number | undefined } |
	{ type: 'ADD_ENTRY'; payload: NewPortRotationEntry } |
	{ type: 'SYNC_FULL'; payload: LocalPortRotation[] } |
	{ type: 'SYNC_PARTIAL'; payload: { centralPortRotation: LocalPortRotation[]; preserveFields: (keyof LocalPortRotation)[] } } |
	{ type: 'CLEAR_PENDING_CHANGES' } |
	{ type: 'CLEAR_PENDING_ORDER_CHANGES' };

const portRotationReducer = (
	state: PortRotationState, action: PortRotationAction,
): PortRotationState => {
	switch (action.type) {
		case 'SET_LOCAL':
			return {
				...state,
				localPortRotation: action.payload,
				...(action.update ? { pendingPortRotationChanges: action.payload ?? [] } : {}),
			};

		case 'UPDATE_ROW': {
			if (state.localPortRotation == null) {
				return state;
			}

			const { id, attributes } = action.payload;
			const index = state.localPortRotation.findIndex((entry) => entry.id === id);

			if (index === -1) {
				return state;
			}

			const updatedRotation = [...state.localPortRotation];
			updatedRotation[index] = { ...updatedRotation[index], ...attributes };

			return {
				...state,
				localPortRotation: updatedRotation,
				pendingPortRotationChanges: updatedRotation,
			};
		}

		case 'UPDATE_ROUTE_OPTIONS': {
			if (state.localPortRotation == null) {
				return state;
			}

			const { id, routeOptions } = action.payload;
			const index = state.localPortRotation.findIndex((entry) => entry.id === id);

			if (index === -1) {
				return state;
			}

			const updatedRotation = [...state.localPortRotation];
			updatedRotation[index] = {
				...updatedRotation[index],
				routeOptions: structuredClone(routeOptions),
			};

			return {
				...state,
				localPortRotation: updatedRotation,
				pendingPortRotationChanges: updatedRotation,
			};
		}

		case 'SET_ORDER': {
			return {
				...state,
				localPortRotation: action.payload,
				pendingPortRotationOrderChanges: action.payload,
			};
		}

		case 'DELETE_ENTRY': {
			if (state.localPortRotation == null) {
				return state;
			}

			const filtered = state.localPortRotation.filter((e) => e.id !== action.payload);

			return {
				...state,
				localPortRotation: filtered,
				pendingPortRotationOrderChanges: filtered,
			};
		}

		case 'ADD_ENTRY': {
			if (state.localPortRotation == null) {
				return state;
			}

			const updated = [...state.localPortRotation, action.payload as unknown as LocalPortRotation];

			return {
				...state,
				localPortRotation: updated,
				pendingPortRotationOrderChanges: updated,
			};
		}

		case 'SYNC_FULL': {
			return {
				...state,
				localPortRotation: action.payload,
			};
		}

		case 'SYNC_PARTIAL': {
			const { centralPortRotation, preserveFields } = action.payload;

			const updated = centralPortRotation.map((serverEntry, index) => {
				const localEntry = state.localPortRotation?.[index];

				if (localEntry == null || localEntry?.port?.id !== serverEntry?.port?.id) {
					return serverEntry;
				}

				const preservedFields: Record<string, any> = {};
				preserveFields.forEach((field) => {
					preservedFields[field] = localEntry[field];
				});

				return {
					...serverEntry,
					...preservedFields,
				};
			});

			return {
				...state,
				localPortRotation: updated.filter((u) => u != null) as LocalPortRotation[],
			};
		}

		case 'CLEAR_PENDING_CHANGES': {
			return {
				...state,
				pendingPortRotationChanges: [],
			};
		}

		case 'CLEAR_PENDING_ORDER_CHANGES': {
			return {
				...state,
				pendingPortRotationOrderChanges: [],
			};
		}

		default:
			return state;
	}
};

type Props = {
	onUpdateFixture: (attributes: UpdateFixtureAttributes) => void;
	estimate: GetEstimateDetailsResponse | null | undefined;
	centralPortRotation: LocalPortRotation[];
	syncPortRotation: 'partial' | 'full' | undefined;
	setSyncPortRotation: React.Dispatch<React.SetStateAction<'partial' | 'full' | undefined>>;
	selectedEstimateId: number | null;
};

export const usePortRotationHandlers = ({
	estimate,
	onUpdateFixture,
	centralPortRotation,
	syncPortRotation,
	setSyncPortRotation,
	selectedEstimateId,
}: Props) => {
	const SYSTEM_DEFAULT_SEA_MARGIN = 5 as const;
	const [userData] = useFetchedState(getUserData);

	const defaultSeaMargin = useMemo(
		() => userData?.defaultSeaMargin ?? SYSTEM_DEFAULT_SEA_MARGIN,
		[userData?.defaultSeaMargin],
	);

	const [state, dispatch] = useReducer(portRotationReducer, {
		localPortRotation: null,
		pendingPortRotationChanges: [],
		pendingPortRotationOrderChanges: [],
	});

	const { localPortRotation, pendingPortRotationChanges, pendingPortRotationOrderChanges } = state;

	const applySeaMarginDefaults = (
		entries: LocalPortRotation[],
		defaultSeaMargin: number,
	): LocalPortRotation[] =>
		entries.map((p) => {
			let seaMargin = p.seaMargin;

			if (seaMargin == null || seaMargin === SYSTEM_DEFAULT_SEA_MARGIN) {
				seaMargin = defaultSeaMargin;
			}

			return {
				...p,
				seaMargin,
			};
		});

	const syncCalculatedFields = useCallback((sync: 'partial' | 'full') => {
		if (sync === 'full') {
			dispatch({ type: 'SYNC_FULL', payload: centralPortRotation });

			return;
		}

		dispatch({
			type: 'SYNC_PARTIAL',
			payload: {
				centralPortRotation,
				preserveFields: ['loadDischargeRate', 'seaMargin', 'idleDays', 'turnDays', 'portCost', 'draft'],
			},
		});
	}, [centralPortRotation, defaultSeaMargin]);

	useEffect(() => {
		if (
			(localPortRotation == null && centralPortRotation != null) ||
			(localPortRotation?.length !== centralPortRotation?.length)
		) {
			const withDefaults = applySeaMarginDefaults(centralPortRotation, defaultSeaMargin);

			dispatch({ type: 'SET_LOCAL', payload: withDefaults });
			setSyncPortRotation(undefined);
		}
	}, [
		centralPortRotation,
		localPortRotation,
		syncPortRotation,
		setSyncPortRotation,
		defaultSeaMargin,
	]);

	useEffect(() => {
		if (syncPortRotation) {
			syncCalculatedFields(syncPortRotation);
			setSyncPortRotation(undefined);
		}
	}, [
		centralPortRotation,
		localPortRotation,
		syncPortRotation,
		setSyncPortRotation,
		syncCalculatedFields,
	]);

	const onPortRotationChange = useCallback(async (
		entries: LocalPortRotation[],
		immediate?: boolean,
	) => {
		if (selectedEstimateId == null) {
			return;
		}

		dispatch({ type: 'SET_LOCAL', payload: entries, update: immediate });
	}, [selectedEstimateId]);

	const onOrderChange = useCallback(async (entries: LocalPortRotation[]) => {
		if (selectedEstimateId == null) {
			return;
		}

		dispatch({ type: 'SET_ORDER', payload: entries });
	}, [selectedEstimateId]);

	const onPortRotationRowChange = useCallback((
		id: number | undefined,
		attributes: PartialPortRotationEntry,
	) => {
		if (selectedEstimateId == null) {
			return;
		}

		if (attributes.generatedBy === PortRotationEntryGeneratorTypes.REDELIVERY_PORT) {
			onUpdateFixture({
				redeliveryPortsOrRanges: attributes.port != null ? [attributes.port] : undefined,
			});
		}

		if (attributes.generatedBy === PortRotationEntryGeneratorTypes.DELIVERY_PORT) {
			onUpdateFixture({
				deliveryPortsOrRanges: attributes.port != null ? [attributes.port] : undefined,
			});
		}

		dispatch({ type: 'UPDATE_ROW', payload: { id, attributes } });
	}, [selectedEstimateId, onUpdateFixture]);

	const onConfigUpdate = useCallback(async (
		id: number | undefined,
		routeOptions: SeaPassageSettings,
	) => {
		if (selectedEstimateId == null) {
			return;
		}

		dispatch({ type: 'UPDATE_ROUTE_OPTIONS', payload: { id, routeOptions } });
	}, [selectedEstimateId]);

	const onDeletePortRotationEntry = useCallback((entryToDelete: PartialPortRotationEntry) => {
		if (selectedEstimateId == null) {
			return;
		}

		dispatch({ type: 'DELETE_ENTRY', payload: entryToDelete.id });
	}, [selectedEstimateId]);

	const onAddPortRotationEntry = useCallback((
		finalFormValues: NewPortRotationEntryFormValues,
		onAdd: (editing: boolean) => void,
	) => {
		if (selectedEstimateId == null || localPortRotation == null) {
			return;
		}

		const matchingCanalUserDefault =
			userData?.orgCanalTransitDefaults?.find(
				(entry: OrgCanalTransitDefaults) => entry.canalTransitId === finalFormValues.portId,
			);

		let idleDays = finalFormValues.daysInPort ?? 0;
		let portCost = finalFormValues.portCost ?? null;

		if (matchingCanalUserDefault) {
			if (matchingCanalUserDefault.applyToAllVessels ||
				(matchingCanalUserDefault.vesselType === estimate?.vessel?.type &&
					matchingCanalUserDefault.vesselSize === estimate.vessel.size)) {
				idleDays = matchingCanalUserDefault.idleTime ?? 0;
				portCost = matchingCanalUserDefault.cost ?? null;
			}
		}

		const newEntry: NewPortRotationEntry = {
			id: -1,
			type: finalFormValues.type!,
			estimateId: selectedEstimateId,
			portCost,
			linerCostPricePerUnit: finalFormValues.linerCostPricePerUnit ?? null,
			generatedBy: null,
			port: { id: finalFormValues.portId } as Port,
			idleDays,
			speed: 12,
			seaMargin: defaultSeaMargin,
			consumptionSetId: finalFormValues.consumptionSetId ?? null,
			PortCallActions: [],
		};

		onAdd(false);
		dispatch({ type: 'ADD_ENTRY', payload: newEntry });
	}, [selectedEstimateId, localPortRotation, userData?.orgCanalTransitDefaults,
		estimate?.vessel?.type, estimate?.vessel?.size, defaultSeaMargin]);

	const portRotationWithKeys = useMemo(() => {
		if (localPortRotation == null) {
			return [];
		}

		return localPortRotation.map((p) => ({
			...p,
			key: `${p.id ?? -1}`,
		}));
	}, [localPortRotation]);

	const intakePortRotation = useMemo(() => {
		return portRotationWithKeys.filter((p) => (
			p.type === PortActionTypes.LOADING ||
			p.type === PortActionTypes.DISCHARGING ||
			p.type === PortActionTypes.CANAL_TRANSIT
		));
	}, [portRotationWithKeys]);

	const setPendingChanges = useCallback((_changes: LocalPortRotation[]) => {
		dispatch({ type: 'CLEAR_PENDING_CHANGES' });
	}, []);

	const setPendingOrderChanges = useCallback((_changes: LocalPortRotation[]) => {
		dispatch({ type: 'CLEAR_PENDING_ORDER_CHANGES' });
	}, []);

	return {
		portRotation: portRotationWithKeys,
		intakePortRotation,
		onPortRotationChange,
		onPortRotationRowChange,
		onOrderChange,
		onConfigUpdate,
		onAddPortRotationEntry,
		onDeletePortRotationEntry,
		pendingChanges: pendingPortRotationChanges,
		setPendingChanges,
		pendingPortRotationOrderChanges,
		setPendingPortRotationOrderChanges: setPendingOrderChanges,
	};
};
